import AppIconButton from "../form/AppIconButton";
import Autocomplete from "@mui/material/Autocomplete";
import { Box, Stack, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useTranslation } from "react-i18next";
import { AppTextField } from "../form";
import {
  fieldOptionToColumn,
  formatTableResultFieldLabel,
  resolveColumnFieldPickerValue,
  tableFieldPickerOptionsEqual,
  type TableFieldPickerOption,
} from "../../utils/tableMappingBuilder";
import type { ChartSliceMapping } from "../../utils/chartMappingBuilder";
import type { TableResultFieldOption } from "../../utils/openSearchTableAnalysis";
import { TABLE_CELL_INPUT_SX, TABLE_ROW_GRID, tableCellFieldProps } from "../TableColumnsEditor/tableCellStyles";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  slices: ChartSliceMapping[];
  fieldOptions: TableResultFieldOption[];
  disabled?: boolean;
  onChange: (slices: ChartSliceMapping[]) => void;
};

export default function ChartSlicesEditor({ slices, fieldOptions, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.tableColumnsEditor;

  const updateSlice = (index: number, patch: Partial<ChartSliceMapping>) => {
    onChange(slices.map((slice, i) => (i === index ? { ...slice, ...patch } : slice)));
  };

  const removeSlice = (index: number) => {
    onChange(slices.filter((_, i) => i !== index));
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerRow}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {t("visualEditor.chartSliceField")}
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {t("visualEditor.chartSliceLabel")}
        </Typography>
        <Box />
      </Box>

      {slices.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={styles.emptyState}>
          {t("visualEditor.chartSlicesEmpty")}
        </Typography>
      )}

      {slices.map((slice, index) => {
        const selected =
          resolveColumnFieldPickerValue({ field: slice.field, path: slice.path }, fieldOptions)[0] ??
          null;

        return (
          <Box key={`${slice.field}-${slice.path}-${index}`} sx={styles.dataRow(index < slices.length - 1)}>
            <Box sx={TABLE_ROW_GRID}>
              <Box sx={styles.fieldCol}>
                <Typography variant="caption" color="text.secondary" sx={styles.mobileLabel}>
                  {t("visualEditor.chartSliceField")}
                </Typography>
                <Autocomplete<TableResultFieldOption, false, false, false>
                  size="small"
                  fullWidth
                  options={fieldOptions}
                  value={selected}
                  onChange={(_, opt) => {
                    if (!opt) return;
                    const next = fieldOptionToColumn(opt);
                    updateSlice(index, {
                      field: next.field,
                      path: next.path,
                      header: slice.header?.trim() ? slice.header : next.header,
                    });
                  }}
                  getOptionLabel={(o) => formatTableResultFieldLabel(o)}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} key={`${o.group || o.field}:${o.path}`}>
                      <Typography variant="body2" component="span" fontWeight={600}>
                        {formatTableResultFieldLabel(o)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={styles.optionTypeCaption}>
                        {o.type}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <AppTextField
                      {...params}
                      {...tableCellFieldProps}
                      label={t("visualEditor.chartSliceField")}
                      placeholder={t("visualEditor.columnFieldPlaceholder")}
                      sx={styles.fieldAutocompleteInput}
                    />
                  )}
                  isOptionEqualToValue={(a, b) => tableFieldPickerOptionsEqual(a, b)}
                  disabled={disabled}
                  sx={styles.fieldAutocomplete}
                />
              </Box>

              <Box sx={styles.fieldCol}>
                <Typography variant="caption" color="text.secondary" sx={styles.mobileLabel}>
                  {t("visualEditor.chartSliceLabel")}
                </Typography>
                <AppTextField
                  {...tableCellFieldProps}
                  label={t("visualEditor.chartSliceLabel")}
                  placeholder={t("visualEditor.chartSliceLabelPlaceholder")}
                  value={slice.header}
                  onChange={(e) => updateSlice(index, { header: e.target.value })}
                  disabled={disabled}
                  sx={TABLE_CELL_INPUT_SX}
                />
              </Box>

              <Box sx={styles.deleteButtonCol}>
                <AppIconButton
                  size="small"
                  color="error"
                  confirmDelete={{ itemName: slice.header || String(index + 1) }}
                  onClick={() => removeSlice(index)}
                  disabled={disabled}
                  aria-label={t("common.delete")}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </AppIconButton>
              </Box>
            </Box>
          </Box>
        );
      })}

      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mt: 1 }}>
        <AppIconButton
          size="small"
          color="primary"
          disabled={disabled || fieldOptions.length === 0}
          onClick={() => {
            const used = new Set(slices.map((s) => `${s.field}:${s.path}`));
            const next = fieldOptions.find((o) => !used.has(`${o.field}:${o.path}`));
            if (!next) return;
            onChange([...slices, fieldOptionToColumn(next)]);
          }}
          aria-label={t("visualEditor.addChartSlice")}
        >
          <AddOutlinedIcon fontSize="small" />
        </AppIconButton>
        <Typography variant="caption" color="text.secondary">
          {t("visualEditor.addChartSlice")}
        </Typography>
      </Stack>
    </Box>
  );
}
